import type { Editor } from "@tiptap/react";
import { Button, cn, Separator } from "@workith/ui";
import {
  Bold,
  Code,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  HelpCircle,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Pencil,
  Quote,
  Redo,
  SquareTerminal,
  Strikethrough,
  Undo,
} from "lucide-react";

interface MenuBarProps {
  editor: Editor | null;
  isEditable?: boolean;
  setIsEditable?: (val: boolean) => void;
  className?: string;
  /**
   * What the toolbar currently acts on. A sectioned document has one editor per
   * section, so the toolbar has to say which one it will command.
   */
  targetLabel?: string;
}

const MenuItem = ({
  onClick,
  disabled,
  isActive,
  children,
  title,
}: {
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  children: React.ReactNode;
  title?: string;
}) => (
  <Button
    variant={isActive ? "default" : "ghost"}
    size="sm"
    onClick={onClick}
    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
    disabled={disabled}
    title={title}
    className={cn("h-8 w-8 p-0", isActive && "bg-primary text-primary-foreground")}
  >
    {children}
  </Button>
);

export default function MenuBar({
  editor,
  isEditable = true,
  setIsEditable,
  className,
  targetLabel,
}: MenuBarProps) {
  if (!editor) {
    return null;
  }

  return (
    <div
      role="toolbar"
      aria-label={targetLabel ? `서식 도구 모음 (${targetLabel})` : "서식 도구 모음"}
      className={cn(
        "border border-border rounded-lg p-2 flex items-center gap-1 flex-wrap",
        className
      )}
    >
      {isEditable ? (
        <>
          {/* Text Formatting */}
          <MenuItem
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title="굵게 (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title="기울임 (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            isActive={editor.isActive("strike")}
            title="취소선"
          >
            <Strikethrough className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!editor.can().chain().focus().toggleCode().run()}
            isActive={editor.isActive("code")}
            title="인라인 코드"
          >
            <Code className="h-4 w-4" />
          </MenuItem>

          <Separator orientation="vertical" className="mx-1 h-6" />

          {/* Headings */}
          <MenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive("heading", { level: 1 })}
            title="제목 1"
          >
            <Heading1 className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive("heading", { level: 2 })}
            title="제목 2"
          >
            <Heading2 className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive("heading", { level: 3 })}
            title="제목 3"
          >
            <Heading3 className="h-4 w-4" />
          </MenuItem>

          <Separator orientation="vertical" className="mx-1 h-6" />

          {/* Lists */}
          <MenuItem
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title="글머리 기호 목록"
          >
            <List className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title="번호 매기기 목록"
          >
            <ListOrdered className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive("blockquote")}
            title="인용"
          >
            <Quote className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive("codeBlock")}
            title="코드 블록"
          >
            <SquareTerminal className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              const previousUrl = editor.getAttributes("link").href;
              const url = window.prompt("링크 URL을 입력하세요:", previousUrl);
              if (url === null) {
                return;
              }
              if (url === "") {
                editor.chain().focus().extendMarkRange("link").unsetLink().run();
                return;
              }
              editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
            }}
            isActive={editor.isActive("link")}
            title="링크 삽입"
          >
            <LinkIcon className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => {
              const url = window.prompt("이미지 URL을 입력하세요:");
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }}
            title="이미지 삽입"
          >
            <ImageIcon className="h-4 w-4" />
          </MenuItem>

          <Separator orientation="vertical" className="mx-1 h-6" />

          {/* Undo/Redo */}
          <MenuItem
            onClick={() => editor.commands.undo()}
            disabled={!editor.can().undo()}
            title="실행 취소 (Ctrl+Z)"
          >
            <Undo className="h-4 w-4" />
          </MenuItem>

          <MenuItem
            onClick={() => editor.commands.redo()}
            disabled={!editor.can().redo()}
            title="다시 실행 (Ctrl+Y)"
          >
            <Redo className="h-4 w-4" />
          </MenuItem>
        </>
      ) : (
        <div className="flex h-8 items-center px-2 text-sm font-medium text-muted-foreground">
          읽기 전용 모드
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        {setIsEditable && (
          <Button
            variant={isEditable ? "ghost" : "secondary"}
            size="sm"
            onClick={() => setIsEditable(!isEditable)}
            className="h-8 text-xs font-medium"
            title={isEditable ? "읽기 모드로 전환" : "편집 모드로 전환"}
          >
            {isEditable ? (
              <Eye className="w-4 h-4 mr-1.5" />
            ) : (
              <Pencil className="w-4 h-4 mr-1.5" />
            )}
            {isEditable ? "읽기 모드" : "편집 모드"}
          </Button>
        )}

        <div className="relative group">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            title="마크다운 단축키 안내"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>

          <div className="absolute right-0 mt-2 w-72 p-4 bg-popover text-popover-foreground border border-border rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              ⌨️ 마크다운 및 단축키 안내
            </h3>

            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                  텍스트 서식 (양옆 감싸기)
                </h4>
                <ul className="space-y-2">
                  <li className="flex justify-between items-center">
                    <span>
                      <strong className="font-bold">굵게</strong>
                    </span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      **텍스트**
                    </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="italic">기울임</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      *텍스트*
                    </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="line-through">취소선</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      ~텍스트~
                    </code>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider mb-2">
                  블록 서식 (맨 앞 입력 후 띄어쓰기)
                </h4>
                <ul className="space-y-2">
                  <li className="flex justify-between items-center">
                    <span>제목 (H1, H2, H3)</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      # , ## , ###{" "}
                    </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>글머리 기호</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      - 또는 *{" "}
                    </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>체크리스트</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                      [] 또는 [x]{" "}
                    </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>인용구</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">&gt; </code>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>코드 블록</span>
                    <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">``` </code>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border text-xs text-muted-foreground text-right">
              Tip: 일반 단축키(Ctrl+B, Ctrl+Z)도 지원합니다.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
